// Community Forum — landing page.
//
// Left rail: category list (with counts + "All").
// Main column: post feed with sort tabs (Hot / New / Unanswered) + search.
// Top-right CTA: "Ask a question" → AskQuestionModal.
//
// Each post card shows score, replies, title, snippet, author, category badge,
// and time. Clicking a card navigates to /app/community/forum/:postId.
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, MessageCircle, Flame, Sparkles, MessageSquareDashed, Search as SearchIcon, Trophy, CheckCircle2 } from "lucide-react";
import AskQuestionModal from "@/components/forum/AskQuestionModal";
import { metaFor, timeAgo } from "@/lib/forumCategories";
import ReputationBadge from "@/components/forum/ReputationBadge";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const SORTS = [
  { id: "hot",        label: "Hot",        icon: Flame },
  { id: "new",        label: "Newest",     icon: Sparkles },
  { id: "unanswered", label: "Unanswered", icon: MessageSquareDashed },
];

// Admin-only — fetches a preview of who would be emailed, asks for
// confirmation, then POSTs to /api/forum/reply-of-the-week/announce.
// Idempotent server-side via (reply_id, week_ending) — re-clicks are safe.
function AnnounceReplyOfWeekButton() {
  const [loading, setLoading] = useState(false);

  const onClick = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const { data: pre } = await api.get("/forum/reply-of-the-week/preview");
      if (!pre.reply) {
        toast.info("No qualifying reply this week yet — wait for some upvotes.");
        return;
      }
      if (pre.already_sent) {
        toast.info(
          `Already announced for week ending ${pre.week_ending} — ${pre.reply.author.display_name}.`,
        );
        return;
      }
      const ok = window.confirm(
        `Email "${pre.reply.author.display_name}" <${pre.reply.author.email}> ` +
        `their reply (${pre.reply.upvotes} upvotes) is the iSpeedToClose ` +
        `Reply of the Week?\n\nThis sends one email and is idempotent for ` +
        `week ending ${pre.week_ending}.`,
      );
      if (!ok) return;
      const { data: res } = await api.post("/forum/reply-of-the-week/announce");
      if (res.already_sent) {
        toast.info("Already announced this week.");
      } else if (res.email_sent) {
        toast.success(`Emailed ${res.author_email}`);
      } else {
        toast.warning(
          "Saved to feature log, but email dispatch failed. Check Resend / domain verification.",
        );
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't announce");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      variant="outline"
      onClick={onClick}
      disabled={loading}
      title="Email the author of this week's top-upvoted forum reply"
      data-testid="announce-reply-of-week-btn"
    >
      <Trophy size={14} className="mr-1" />
      {loading ? "Working…" : "Announce reply of week"}
    </Button>
  );
}

function CategoryRail({ categories, active, onSelect }) {
  return (
    <aside className="lg:w-64 lg:flex-shrink-0" data-testid="forum-category-rail">
      <div className="bg-white border rounded-lg p-2 sticky top-4">
        <button
          type="button"
          onClick={() => onSelect("")}
          className={
            "w-full text-left px-3 py-2 rounded-md text-sm flex items-center gap-2 " +
            (active === "" ? "bg-zinc-900 text-white" : "text-zinc-700 hover:bg-zinc-100")
          }
          data-testid="forum-category-all"
        >
          <MessageCircle size={14} />
          <span className="font-medium">All Questions</span>
        </button>
        <div className="my-1 border-t" />
        {categories.map((c) => {
          const meta = metaFor(c.id);
          const Icon = meta.icon;
          const selected = active === c.id;
          return (
            <button
              key={c.id}
              type="button"
              onClick={() => onSelect(c.id)}
              className={
                "w-full text-left px-3 py-2 rounded-md text-sm flex items-center gap-2 " +
                (selected ? "bg-zinc-900 text-white" : "text-zinc-700 hover:bg-zinc-100")
              }
              title={c.blurb}
              data-testid={`forum-category-${c.id}`}
            >
              <Icon size={14} style={selected ? {} : { color: meta.accent }} />
              <span className="truncate">{c.label}</span>
            </button>
          );
        })}
      </div>
    </aside>
  );
}

function PostCard({ post }) {
  const meta = metaFor(post.category);
  const Icon = meta.icon;
  const snippet = (post.body || "").replace(/[#*_`>\-]/g, "").slice(0, 220);
  return (
    <Link
      to={`/app/community/forum/${post.id}`}
      className="block bg-white border rounded-lg p-4 hover:border-zinc-400 transition"
      data-testid={`forum-post-card-${post.id}`}
    >
      <div className="flex items-start gap-4">
        <div className="hidden sm:flex flex-col items-center w-12 flex-shrink-0">
          <div className={
            "text-sm font-bold " +
            (post.score > 0 ? "text-emerald-700"
              : post.score < 0 ? "text-rose-700" : "text-zinc-500")
          }>
            {post.score >= 0 ? `+${post.score}` : post.score}
          </div>
          <div className="text-[10px] uppercase tracking-wide text-zinc-400">votes</div>
          <div className="mt-2 text-xs text-zinc-600 font-medium">{post.reply_count}</div>
          <div className="text-[10px] uppercase tracking-wide text-zinc-400">replies</div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span
              className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border"
              style={{ borderColor: meta.accent + "55", color: meta.accent, background: meta.accent + "10" }}
            >
              <Icon size={11} /> {post.category.replace(/-/g, " ")}
            </span>
            {(post.tags || []).slice(0, 3).map((t) => (
              <span key={t} className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-600">
                #{t}
              </span>
            ))}
          </div>
          <h3 className="font-semibold text-zinc-900 leading-snug">{post.title}</h3>
          <p className="text-sm text-zinc-600 mt-1 line-clamp-2">{snippet}</p>
          <div className="flex items-center gap-3 mt-2 text-xs text-zinc-500">
            <span className="flex items-center gap-1">
              <span className={"w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white " + (post.author?.is_system ? "bg-amber-500" : "bg-zinc-700")}>
                {(post.author?.display_name || "?")[0].toUpperCase()}
              </span>
              {post.author?.display_name || "anon"}
              {post.author?.is_system && (
                <span className="text-amber-600 text-[10px] font-semibold uppercase">community</span>
              )}
              <ReputationBadge reputation={post.author?.reputation} compact />
            </span>
            <span>·</span>
            <span>{timeAgo(post.created_at)}</span>
            <span className="sm:hidden">·</span>
            <span className="sm:hidden">{post.reply_count} replies</span>
          </div>
        </div>
      </div>
    </Link>
  );
}

export default function ForumIndex() {
  const nav = useNavigate();
  const { user } = useAuth();
  const isAdmin = (user?.role || "").toLowerCase() === "admin";
  const [params, setParams] = useSearchParams();
  const [categories, setCategories] = useState([]);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [askOpen, setAskOpen] = useState(false);
  const [searchInput, setSearchInput] = useState(params.get("q") || "");

  const category = params.get("category") || "";
  const sort = params.get("sort") || "hot";
  const q = params.get("q") || "";

  const setParam = (k, v) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v); else next.delete(k);
    setParams(next, { replace: true });
  };

  const loadCategories = useCallback(async () => {
    try {
      const { data } = await api.get("/forum/categories");
      setCategories(data.items || []);
    } catch { /* ignore */ }
  }, []);

  const loadPosts = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/forum/posts", {
        params: { category: category || undefined, sort, q: q || undefined, limit: 30 },
      });
      setPosts(data.items || []);
    } catch {
      setPosts([]);
    } finally {
      setLoading(false);
    }
  }, [category, sort, q]);

  useEffect(() => { loadCategories(); }, [loadCategories]);
  useEffect(() => { loadPosts(); }, [loadPosts]);

  // Debounce search input → URL.
  useEffect(() => {
    const id = setTimeout(() => {
      if (searchInput !== q) setParam("q", searchInput.trim());
    }, 350);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const activeCategoryLabel = useMemo(() => {
    if (!category) return "All Questions";
    return categories.find((c) => c.id === category)?.label || category;
  }, [category, categories]);

  return (
    <div className="px-4 py-6 max-w-6xl mx-auto" data-testid="forum-index-page">
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-zinc-900">Community Forum</h1>
          <p className="text-sm text-zinc-600 mt-1">
            Ask questions, share what&apos;s working, learn from other wholesalers. Deals stay on the{" "}
            <Link to="/app/community" className="underline">Deal Wall</Link>.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && <AnnounceReplyOfWeekButton />}
          <Button onClick={() => setAskOpen(true)} data-testid="ask-question-btn">
            <Plus size={16} className="mr-1" /> Ask a question
          </Button>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        <CategoryRail
          categories={categories}
          active={category}
          onSelect={(id) => setParam("category", id)}
        />

        <main className="flex-1 min-w-0">
          {/* Toolbar */}
          <div className="bg-white border rounded-lg p-3 flex items-center gap-2 mb-4 flex-wrap">
            <div className="flex items-center gap-1">
              {SORTS.map((s) => {
                const Icon = s.icon;
                const active = sort === s.id;
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => setParam("sort", s.id)}
                    className={
                      "px-2.5 py-1.5 text-xs rounded-md flex items-center gap-1 " +
                      (active ? "bg-zinc-900 text-white" : "text-zinc-700 hover:bg-zinc-100")
                    }
                    data-testid={`forum-sort-${s.id}`}
                  >
                    <Icon size={12} /> {s.label}
                  </button>
                );
              })}
            </div>
            <div className="flex-1 min-w-[180px] relative">
              <SearchIcon size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
              <Input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search questions…"
                className="pl-7 h-8 text-xs"
                data-testid="forum-search-input"
              />
            </div>
          </div>

          <div className="text-xs text-zinc-500 mb-3" data-testid="forum-result-summary">
            {loading ? "Loading…" : `${posts.length} question${posts.length === 1 ? "" : "s"} in ${activeCategoryLabel}`}
          </div>

          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-24 bg-white border rounded-lg animate-pulse" />
              ))}
            </div>
          ) : posts.length === 0 ? (
            <div className="bg-white border rounded-lg p-8 text-center" data-testid="forum-empty-state">
              <MessageCircle size={36} className="mx-auto text-zinc-300 mb-2" />
              <div className="text-zinc-700 font-medium">No questions here yet.</div>
              <div className="text-xs text-zinc-500 mt-1">Be the first to start a conversation.</div>
              <Button className="mt-4" onClick={() => setAskOpen(true)} data-testid="forum-empty-ask-btn">
                <Plus size={14} className="mr-1" /> Ask a question
              </Button>
            </div>
          ) : (
            <div className="space-y-3" data-testid="forum-post-list">
              {posts.map((p) => <PostCard key={p.id} post={p} />)}
            </div>
          )}
        </main>
      </div>

      <AskQuestionModal
        open={askOpen}
        onOpenChange={setAskOpen}
        categories={categories}
        defaultCategory={category}
      />
    </div>
  );
}
